// Receipts store (Zustand) — Phase 1.
// Holds two lists: the always-on "to review" queue (every pending receipt,
// unbounded by month) and the month-scoped "submitted" archive. Plus the
// nav-badge count and a cached form-options lookup. Individual receipt detail
// stays page-local (matches OrdersPage / AssetsPage). All actions wrap apiFetchAuthed.

import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';
import { Receipt, FormOptions, ReceiptLineItem, EventOption, SubmitCleanResult, MissingCharge, CardholderResponseKind } from '../Receipts/types';

// The action queue — pending work, sent in full regardless of month.
export const QUEUE_STATUSES = ['uploaded', 'processing', 'processing_failed', 'needs_review'];
// The archive — paginated one calendar month at a time.
export const SUBMITTED_STATUSES = ['submitted', 'approved', 'exported', 'synced'];

// The search bar's filters. Any one set turns the list call into an all-time,
// all-status search; the server caps it at SEARCH_LIMIT rows (its own constant,
// mirrored here only so the page can say "showing the first N").
export interface ReceiptSearch {
  q?: string;       // free text: vendor, owner name, note (terms AND together)
  amount?: string;  // exact to the cent, on total or subtotal
  from?: string;    // 'YYYY-MM-DD', inclusive
  to?: string;      // 'YYYY-MM-DD', inclusive
}
export const SEARCH_LIMIT = 200;
// Responses may land out of order under a debounce; only the newest one wins.
let searchSeq = 0;

// Buyer "review all" flow: a snapshot of reviewable public_ids taken when the
// buyer taps "Review all" on the Needs-your-eyes group. In-memory only (a page
// refresh clears it, and the review page just drops its flow chrome). `index`
// tracks the receipt currently under review and stays in lockstep with the
// routed pid — every forward move goes through advanceFlow.
interface ReviewFlow {
  ids: string[];   // snapshot at start; a receipt drained by someone else mid-flow just 404s and we advance past it
  index: number;
}

interface ReceiptsState {
  toReview: Receipt[];
  toReviewOther: Receipt[];  // receipts I scanned that now belong to someone else; I may still review them
  submitted: Receipt[];
  results: Receipt[];        // search results, all months and statuses
  searchError: string | null; // the last search failed; the page must not read [] as "nothing matches"
  loadingQueue: boolean;
  loadingMonth: boolean;
  loadingSearch: boolean;
  formOptions: FormOptions | null;
  reviewFlow: ReviewFlow | null;
  missingCharges: MissingCharge[]; // my own card charges with no receipt, oldest first (server-sorted)

  fetchQueue: (scope?: 'mine' | 'all') => Promise<void>;         // scope=all is financial-only (server-enforced)
  fetchMonth: (month: string, scope?: 'mine' | 'all') => Promise<void>; // month = 'YYYY-MM'
  searchReceipts: (params: ReceiptSearch, scope?: 'mine' | 'all') => Promise<void>;
  clearSearch: () => void;
  ensureFormOptions: (receiptPid?: string) => Promise<void>; // receiptPid: offer that receipt's owner's cards

  startFlow: (ids: string[]) => void;            // snapshot the deck, begin at the first
  advanceFlow: () => string | null;              // next pid, or null (+ clears the flow) at the end
  exitFlow: () => void;                           // leave the flow, stay put
  syncFlowIndex: (pid: string) => void;           // realign index to the routed pid on (re)mount

  fetchMissingCharges: () => Promise<void>;
  // Both replace the row in missingCharges from the server's reply. silent: the caller toasts.
  respondToCharge: (id: number, response: CardholderResponseKind, note?: string) => Promise<MissingCharge>;
  clearChargeResponse: (id: number) => Promise<MissingCharge>;

  // forChargeId: the receipt is for that exact charge; the server links it as a suggested match.
  upload: (file: File, cls?: { classId?: number | null; deferred?: boolean }, forChargeId?: number) => Promise<Receipt>;
  getReceipt: (pid: string) => Promise<Receipt>;
  patchReceipt: (pid: string, patch: Partial<Receipt>) => Promise<Receipt>;
  replaceLineItems: (pid: string, items: ReceiptLineItem[]) => Promise<Receipt>;
  submitReceipt: (pid: string) => Promise<Receipt>;
  // bulk-confirm the caller's clean rows ("Ready to confirm"); scope narrows which group
  submitClean: (scope?: 'mine' | 'scanned' | 'received' | 'uploaded') => Promise<SubmitCleanResult>;
  reassignReceipt: (pid: string, toUserId: string) => Promise<Receipt>;
  replacePhoto: (pid: string, file: File) => Promise<Receipt>; // "retake": swap the image in place
  // "AI Rescan": re-read the SAME image. `hint` is one optional line about what the
  // reader got wrong; the API treats it as untrusted data, never as instructions.
  // Returns the row already flipped to 'processing' so the caller can show a spinner.
  rescanReceipt: (pid: string, hint?: string) => Promise<Receipt>;
  deleteReceipt: (pid: string) => Promise<void>; // hard delete (204, no body)
  fetchEventOptions: (params: { date?: string; q?: string }) => Promise<EventOption[]>;
  dismissDuplicate: (pid: string) => Promise<{ receipt: Receipt }>; // clears likely_duplicate; 409 if none is flagged
}

const useReceiptsStore = create<ReceiptsState>((set, get) => ({
  toReview: [],
  toReviewOther: [],
  submitted: [],
  results: [],
  searchError: null,
  loadingQueue: false,
  loadingMonth: false,
  loadingSearch: false,
  formOptions: null,
  reviewFlow: null,
  missingCharges: [],

  startFlow: (ids) => {
    if (!ids.length) return;
    set({ reviewFlow: { ids, index: 0 } });
  },

  advanceFlow: () => {
    const flow = get().reviewFlow;
    if (!flow) return null;
    const next = flow.index + 1;
    if (next >= flow.ids.length) { set({ reviewFlow: null }); return null; }
    set({ reviewFlow: { ...flow, index: next } });
    return flow.ids[next];
  },

  exitFlow: () => set({ reviewFlow: null }),

  syncFlowIndex: (pid) => {
    const flow = get().reviewFlow;
    if (!flow) return;
    const pos = flow.ids.indexOf(pid);
    if (pos >= 0 && pos !== flow.index) set({ reviewFlow: { ...flow, index: pos } });
  },

  fetchQueue: async (scope = 'mine') => {
    set({ loadingQueue: true });
    try {
      const s = scope === 'all' ? '&scope=all' : '';
      const [data, other]: Receipt[][] = await Promise.all([
        apiFetchAuthed(`/receipts?status=${QUEUE_STATUSES.join(',')}${s}`),
        // The Everyone view is already a flat list of everyone's receipts.
        scope === 'all' ? [] : apiFetchAuthed(`/receipts?scope=uploaded&status=${QUEUE_STATUSES.join(',')}`),
      ]);
      set({ toReview: data, toReviewOther: other });
    } finally {
      set({ loadingQueue: false });
    }
  },

  fetchMonth: async (month, scope = 'mine') => {
    set({ loadingMonth: true });
    try {
      const s = scope === 'all' ? '&scope=all' : '';
      const data: Receipt[] = await apiFetchAuthed(`/receipts?status=${SUBMITTED_STATUSES.join(',')}&month=${month}${s}`);
      set({ submitted: data });
    } finally {
      set({ loadingMonth: false });
    }
  },

  searchReceipts: async (params, scope = 'mine') => {
    const seq = ++searchSeq;
    set({ loadingSearch: true });
    try {
      const p = new URLSearchParams();
      if (params.q) p.set('q', params.q);
      if (params.amount) p.set('amount', params.amount);
      if (params.from) p.set('from', params.from);
      if (params.to) p.set('to', params.to);
      if (scope === 'all') p.set('scope', 'all');
      const data: Receipt[] = await apiFetchAuthed(`/receipts?${p.toString()}`);
      if (seq === searchSeq) set({ results: data, searchError: null });
    } catch (e: any) {
      if (seq === searchSeq) set({ results: [], searchError: e?.message || 'Search failed' });
    } finally {
      if (seq === searchSeq) set({ loadingSearch: false });
    }
  },

  clearSearch: () => { searchSeq += 1; set({ results: [], searchError: null, loadingSearch: false }); },

  // Refetch each call (not cached) — card ownership changes in Admin must be
  // reflected, or a stale dropdown offers a card that's since become someone else's.
  ensureFormOptions: async (receiptPid) => {
    const data: FormOptions = await apiFetchAuthed(`/receipts/form-options${receiptPid ? `?receipt=${receiptPid}` : ''}`);
    set({ formOptions: data });
  },

  fetchMissingCharges: async () => {
    const data: { charges: MissingCharge[] } = await apiFetchAuthed('/receipts/missing-charges', { silent: true });
    set({ missingCharges: data?.charges || [] });
  },

  respondToCharge: async (id, response, note) => {
    const body: { response: CardholderResponseKind; note?: string } = { response };
    if (note) body.note = note;
    const { charge }: { charge: MissingCharge } = await apiFetchAuthed(`/receipts/charges/${id}/response`, { method: 'POST', body, silent: true });
    set((s) => ({ missingCharges: s.missingCharges.map((c) => (c.id === charge.id ? charge : c)) }));
    return charge;
  },

  clearChargeResponse: async (id) => {
    const { charge }: { charge: MissingCharge } = await apiFetchAuthed(`/receipts/charges/${id}/response`, { method: 'DELETE', silent: true });
    set((s) => ({ missingCharges: s.missingCharges.map((c) => (c.id === charge.id ? charge : c)) }));
    return charge;
  },

  upload: async (file, cls, forChargeId) => {
    const fd = new FormData();
    fd.append('file', file);
    // "Bought for" class captured at scan. Deferred ("Not sure") wins over a pick;
    // absent, the backend defers it to "Not sure" (the slider always sends one, so a
    // blank only happens for non-interactive intake).
    if (cls?.deferred) fd.append('class_deferred', 'true');
    else if (cls?.classId != null) fd.append('purchase_class_id', String(cls.classId));
    if (forChargeId != null) fd.append('for_charge_id', String(forChargeId));
    // silent: the caller handles 409 (duplicate) + surfaces its own toast.
    return apiFetchAuthed('/receipts', { method: 'POST', body: fd, silent: true });
  },

  getReceipt: (pid) => apiFetchAuthed(`/receipts/${pid}`),

  patchReceipt: (pid, patch) =>
    apiFetchAuthed(`/receipts/${pid}`, { method: 'PATCH', body: patch }),

  replaceLineItems: (pid, items) =>
    apiFetchAuthed(`/receipts/${pid}/line-items`, { method: 'PUT', body: { line_items: items } }),

  submitReceipt: (pid) =>
    apiFetchAuthed(`/receipts/${pid}/submit`, { method: 'POST' }),

  submitClean: (scope = 'mine') =>
    apiFetchAuthed('/receipts/submit-clean', { method: 'POST', body: { scope } }),

  reassignReceipt: (pid, toUserId) =>
    apiFetchAuthed(`/receipts/${pid}/reassign`, { method: 'POST', body: { to_user_id: toUserId } }),

  replacePhoto: (pid, file) => {
    const fd = new FormData();
    fd.append('file', file);
    // silent: the caller surfaces its own toast + spinner (mirrors upload()).
    return apiFetchAuthed(`/receipts/${pid}/image`, { method: 'POST', body: fd, silent: true });
  },

  rescanReceipt: (pid, hint) =>
    // silent: the caller surfaces its own toast + spinner (mirrors replacePhoto).
    apiFetchAuthed(`/receipts/${pid}/rescan`, { method: 'POST', body: hint ? { hint } : {}, silent: true }),

  deleteReceipt: (pid) =>
    apiFetchAuthed(`/receipts/${pid}`, { method: 'DELETE', silent: true }),

  fetchEventOptions: ({ date, q }) => {
    const p = new URLSearchParams();
    if (date) p.set('date', date);
    if (q) p.set('q', q);
    return apiFetchAuthed(`/receipts/event-options?${p.toString()}`);
  },

  dismissDuplicate: (pid) =>
    apiFetchAuthed(`/receipts/${pid}/dismiss-duplicate`, { method: 'POST' }),
}));

export default useReceiptsStore;
